import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_META, ROLES, type Role } from '@accel/shared';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth, useSession } from '../auth/AuthContext';
import type { Party } from '../lib/types';
import { Card, EmptyState, ErrorNote, PageHead, Spinner, Tag, formatDate } from '../components/ui';

interface Membership {
  id: string;
  role: Role;
  active: boolean;
  isDefault: boolean;
  party: { id: string; name: string; code: string; kind: string } | null;
}

interface Person {
  user: {
    id: string;
    email: string;
    name: string;
    active: boolean;
    lastLoginAt: string | null;
    createdAt: string;
  };
  memberships: Membership[];
}

/** What a newly created account needs to be told to its owner. */
interface Created {
  name: string;
  email: string;
  temporaryPassword?: string;
}

/**
 * Access administration.
 *
 * Two things are being created here and they are not the same: a person, and
 * the role that person holds. The form keeps them together because that is
 * how the work arrives — "add a login for Acme's finance team" — but the API
 * writes a user and a membership, and a second role can be added later
 * without a second login.
 */
export function UsersPage() {
  const qc = useQueryClient();
  const session = useSession();
  const { viewAs } = useAuth();
  const navigate = useNavigate();

  const people = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<Person[]>('/users'),
  });
  const parties = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/parties'),
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('SME');
  const [partyId, setPartyId] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<Created | null>(null);

  // Which counterparty a role must belong to, straight from the shared role
  // table the server checks against — so the form cannot offer a pairing the
  // API would reject.
  const requiredKind = ROLE_META[role].partyKind;
  const eligible = (parties.data ?? []).filter((p) => p.kind === requiredKind && p.active !== false);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ name: string; email: string; temporaryPassword?: string }>('/users', {
        name: name.trim(),
        email: email.trim(),
        role,
        ...(requiredKind ? { partyId } : {}),
      }),
    onSuccess: (result) => {
      setCreated(result);
      setError(null);
      setName('');
      setEmail('');
      setPartyId('');
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setCreated(null);
      setError(err);
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/users/${id}`, { active }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(err),
  });

  const impersonate = useMutation({
    mutationFn: (person: Person) => viewAs(person.user.id),
    // The borrowed session has that person's navigation, not the
    // administrator's, so land somewhere every role can see.
    onSuccess: () => navigate('/'),
    onError: (err) => setError(err),
  });

  const resetPassword = useMutation({
    mutationFn: (person: Person) =>
      api
        .post<{ temporaryPassword?: string }>(`/users/${person.user.id}/password`, {})
        .then((r) => ({ ...r, name: person.user.name, email: person.user.email })),
    onSuccess: (result) => {
      setCreated({
        name: result.name,
        email: result.email,
        temporaryPassword: result.temporaryPassword,
      });
      setError(null);
    },
    onError: (err) => setError(err),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setCreated(null);

    if (!name.trim()) return setError(new Error('Enter the person’s name'));
    if (!email.trim()) return setError(new Error('Enter their email address'));
    if (requiredKind && !partyId) {
      return setError(new Error(`Choose the ${requiredKind.toLowerCase()} this login acts for`));
    }
    create.mutate();
  }

  if (people.isLoading) return <Spinner label="Loading people" />;
  if (people.error) return <ErrorNote error={people.error} />;

  const rows = people.data ?? [];

  return (
    <>
      <PageHead
        crumb="Configuration"
        title="People and access"
        sub="A person signs in once and holds one or more roles. Deactivating takes effect on their next request, not their next login."
      />

      <ErrorNote error={error} />

      {created ? (
        <div
          className="notice"
          style={{
            background: 'var(--amber-soft)',
            color: 'var(--amber)',
            padding: '12px 14px',
            borderRadius: 'var(--r-sm)',
            marginBottom: 16,
            fontSize: 13.5,
          }}
        >
          <strong>{created.name}</strong> — {created.email}
          {created.temporaryPassword ? (
            <>
              <br />
              Temporary password: <strong style={{ fontSize: 15 }}>{created.temporaryPassword}</strong>
              <br />
              {/* Shown once because it is never stored in plaintext, and there
                  is no email provider wired to send it for us. */}
              Write this down and give it to them directly. It cannot be shown again.
            </>
          ) : (
            <>
              <br />
              Password set.
            </>
          )}
        </div>
      ) : null}

      <Card title="Add someone">
        <form onSubmit={submit}>
          <div className="grid-2">
            <div className="field">
              <label htmlFor="user-name">Name</label>
              <input
                id="user-name"
                value={name}
                placeholder="Wanjiku Mwangi"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="user-email">Email</label>
              <input
                id="user-email"
                type="email"
                value={email}
                placeholder="finance@acme.example"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="user-role">Role</label>
              <select
                id="user-role"
                className="select"
                value={role}
                onChange={(e) => {
                  setRole(e.target.value as Role);
                  setPartyId('');
                }}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_META[r].name}
                  </option>
                ))}
              </select>
              <span className="cell-t2">{ROLE_META[role].description}</span>
            </div>

            {requiredKind ? (
              <div className="field">
                <label htmlFor="user-party">
                  {requiredKind === 'SME' ? 'Supplier' : requiredKind === 'PAYER' ? 'Payer' : 'Funder'}
                </label>
                <select
                  id="user-party"
                  className="select"
                  value={partyId}
                  onChange={(e) => setPartyId(e.target.value)}
                >
                  <option value="">Choose one…</option>
                  {eligible.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="cell-t2">
                  This login sees only what belongs to that organisation.
                </span>
              </div>
            ) : (
              <div className="field">
                <label>Counterparty</label>
                <span className="cell-t2" style={{ paddingTop: 9, display: 'block' }}>
                  {ROLE_META[role].name} acts for the platform, so there is none.
                </span>
              </div>
            )}
          </div>

          <button className="btn btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create login'}
          </button>
          <span className="cell-t2" style={{ marginLeft: 12 }}>
            A password is generated and shown once.
          </span>
        </form>
      </Card>

      <div className="section-title">Everyone with access</div>
      <Card padded={false}>
        {rows.length === 0 ? (
          <EmptyState title="Nobody yet" />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Person</th>
                <th>Roles</th>
                <th>Last signed in</th>
                <th style={{ width: 210 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(({ user, memberships }) => {
                const isSelf = user.id === session.user.id;
                return (
                  <tr key={user.id} style={{ cursor: 'default', opacity: user.active ? 1 : 0.55 }}>
                    <td>
                      <span className="cell-t1">{user.name}</span>
                      <span className="cell-t2" style={{ display: 'block' }}>
                        {user.email}
                        {isSelf ? ' · you' : ''}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {memberships.map((m) => (
                          <Tag key={m.id} tone={m.active ? 'info' : 'neutral'}>
                            {ROLE_META[m.role].name}
                            {m.party ? ` · ${m.party.code}` : ''}
                          </Tag>
                        ))}
                        {!user.active ? <Tag tone="danger">Account off</Tag> : null}
                      </span>
                    </td>
                    <td className="cell-t2">
                      {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {/* Not offered for another administrator, or for
                          yourself — the server refuses both, and a button
                          that always fails is worse than no button. */}
                      {isSelf ||
                      !user.active ||
                      memberships.some((m) => m.active && m.role === 'PLATFORM_ADMIN') ? null : (
                        <button
                          className="btn btn-ghost"
                          disabled={impersonate.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                `View the console as ${user.name}? The session lasts 15 minutes and everything you do is recorded against both of you.`,
                              )
                            ) {
                              impersonate.mutate({ user, memberships });
                            }
                          }}
                        >
                          View as
                        </button>
                      )}
                      <button
                        className="btn btn-ghost"
                        disabled={resetPassword.isPending}
                        onClick={() => {
                          if (window.confirm(`Reset the password for ${user.name}? They will be signed out.`)) {
                            resetPassword.mutate({ user, memberships });
                          }
                        }}
                      >
                        Reset password
                      </button>
                      {/* An administrator locking themselves out is refused by
                          the server; not offering it is the kinder half. */}
                      {isSelf ? null : (
                        <button
                          className="btn btn-ghost"
                          disabled={setActive.isPending}
                          onClick={() =>
                            setActive.mutate({ id: user.id, active: !user.active })
                          }
                        >
                          {user.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
